import { useLayoutEffect, useRef, type RefObject } from "react";

const capturedSubtrees = new WeakSet<HTMLElement>();

/** Retains a noninteractive visual copy when a deleted node leaves its model collection. */
export function useDeletedNodeAnimation(ref: RefObject<HTMLElement>, object: { isDeleted: boolean }): void {
  const animated = useRef(false);
  const running = useRef<Animation>();
  const animate = () => {
    const element = ref.current;
    if (!object.isDeleted || !element?.parentElement || animated.current) return;
    animated.current = true;
    for (let parent: HTMLElement | null = element.parentElement; parent; parent = parent.parentElement) {
      if (capturedSubtrees.has(parent)) return;
    }
    capturedSubtrees.add(element);
    const host = element.closest<HTMLElement>(".connection-dashboard__scroller, .navigationTreeScroller");
    const bounds = element.getBoundingClientRect();
    const ghost = element.cloneNode(true) as HTMLElement;
    ghost.setAttribute("aria-hidden", "true");
    ghost.inert = true;
    ghost.querySelectorAll(".treeMenuButtonsWrapper, .connection-dashboard__hover-placeholder").forEach(node => node.remove());
    for (const node of [ghost, ...ghost.querySelectorAll<HTMLElement>("[id], [popover], [data-connection-dashboard-hover]")]) {
      node.removeAttribute("id");
      node.removeAttribute("popover");
      node.removeAttribute("data-connection-dashboard-hover");
    }
    const height = element.getBoundingClientRect().height;
    Object.assign(ghost.style, { pointerEvents: "none", overflow: "hidden", minHeight: "0", textDecoration: "line-through", textDecorationColor: "red", background: "white" });
    element.before(ghost);
    const duration = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 1200;
    const animation = ghost.animate([
      { height: `${height}px`, opacity: 1 },
      { height: `${height}px`, opacity: 0.65, color: "white", offset: 0.25 },
      { height: "0px", opacity: 0, color: "white", marginTop: "0px", marginBottom: "0px", paddingTop: "0px", paddingBottom: "0px" },
    ], { delay: 1000, duration, easing: "ease-in-out", fill: "both" });
    running.current = animation;
    animation.onfinish = () => ghost.remove();
    animation.oncancel = () => ghost.remove();
    // Removing the last child may also remove its synthetic room/floor group.
    // Preserve its visual exit in the surviving scroller in that case.
    requestAnimationFrame(() => {
      if (!ghost.isConnected && host?.isConnected && animation.playState === "running") {
        Object.assign(ghost.style, { position: "fixed", left: `${bounds.left}px`, top: `${bounds.top}px`, width: `${bounds.width}px`, zIndex: "100" });
        host.appendChild(ghost);
      }
    });
  };
  useLayoutEffect(() => {
    if (!object.isDeleted) {
      animated.current = false;
      running.current?.cancel();
      running.current = undefined;
      if (ref.current) capturedSubtrees.delete(ref.current);
    }
    if (object.isDeleted) {
      animate();
      if (ref.current) ref.current.style.display = "none";
    } else if (ref.current) ref.current.style.removeProperty("display");
  });
  useLayoutEffect(() => () => animate(), [object]);
}
